import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ArrowLeft, Check } from 'lucide-react';
import { trackFBCustom } from './CookieConsent';

/**
 * Hackathon promo pop-up — mounted globally in App.js.
 *
 * Triggers (whichever fires first):
 *   1. 8-second time delay after mount
 *   2. Exit-intent on desktop (mouse leaves through the top of the viewport)
 *
 * Frequency rules:
 *   • Hide if dismissed within the last 24h  (localStorage `hackathon_popup_dismissed_at`)
 *   • Hide for the rest of the session if dismissed in this tab  (sessionStorage)
 *   • Hard cutoff: never shows after 30.6.26 17:00 Israel time (IDT, UTC+3)
 *
 * Excluded routes (where the popup adds no value):
 *   • /hackathon, /hackathon-test, /live-workshop-june-2026  (already on destination)
 *   • /admin*                                                (internal tool)
 */

// Event ends 30.6.26 17:00 IDT (UTC+3). Use ISO with explicit offset.
const CUTOFF_MS = new Date('2026-06-30T17:00:00+03:00').getTime();
const DELAY_MS = 8000; // 8 seconds
const DAY_MS = 24 * 60 * 60 * 1000;

const STORAGE_KEY = 'hackathon_popup_dismissed_at';
const SESSION_KEY = 'hackathon_popup_session_dismissed';

const EXCLUDED_PATHS = [
  '/hackathon',
  '/hackathon-test',
  '/live-workshop-june-2026',
];

const isExcluded = (pathname) => {
  if (pathname.startsWith('/admin')) return true;
  return EXCLUDED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
};

const shouldShow = () => {
  if (Date.now() > CUTOFF_MS) return false; // Past event cutoff
  try {
    if (sessionStorage.getItem(SESSION_KEY) === '1') return false;
    const last = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    if (last && Date.now() - last < DAY_MS) return false;
  } catch { /* storage unavailable */ }
  return true;
};

const HackathonPopup = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Build the CTA URL with UTM attribution at click time. Encoding the source
  // page lets admin see EXACTLY which page sent the lead from the popup.
  const ctaHref = useMemo(() => {
    // utm_content = source page path (sanitized — no leading slash, no '/admin', no querystring)
    const sourcePage = (location.pathname || '/').replace(/^\/+/, '') || 'home';
    const params = new URLSearchParams({
      utm_source: 'hackathon_popup',
      utm_medium: 'popup',
      utm_campaign: 'hackathon_2026_06_30',
      utm_content: sourcePage,
    });
    return `/hackathon?${params.toString()}`;
  }, [location.pathname]);

  const dismiss = useCallback((reason = 'close') => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch { /* silent */ }
    trackFBCustom('Hackathon_Popup_Dismiss', {
      reason,
      source_page: location.pathname || '/',
    });
  }, [location.pathname]);

  const handleCtaClick = useCallback(() => {
    // The user is converting — record click and let them flow into the form.
    // Mark session-dismissed so the popup doesn't re-pop while they fill the form.
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* silent */ }
    trackFBCustom('Hackathon_Popup_CTA_Click', {
      source_page: location.pathname || '/',
      destination: '/hackathon',
    });
  }, [location.pathname]);

  useEffect(() => {
    if (isExcluded(location.pathname)) return undefined;
    if (!shouldShow()) return undefined;

    let timerId;
    let triggered = false;

    const fire = (trigger) => {
      if (triggered) return;
      triggered = true;
      setOpen(true);
      trackFBCustom('Hackathon_Popup_View', {
        trigger,
        source_page: location.pathname || '/',
      });
    };

    // Trigger 1: time delay
    timerId = setTimeout(() => fire('timer'), DELAY_MS);

    // Trigger 2: exit-intent (desktop only — mobile has no mouseout)
    const onMouseOut = (e) => {
      // Mouse left through the TOP of the viewport (going to address bar / tabs)
      if (e.clientY <= 0 && !e.relatedTarget) fire('exit_intent');
    };
    document.addEventListener('mouseout', onMouseOut);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, [location.pathname]);

  // Lock body scroll while open + ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') dismiss('escape'); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="hack-popup-title"
      className="fixed inset-0 z-[10000] flex items-center justify-center px-4 py-6 sm:py-8 animate-fade-in"
      dir="rtl"
      data-testid="hackathon-popup"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="סגירת הפופאפ"
        onClick={() => dismiss('backdrop')}
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        data-testid="hackathon-popup-backdrop"
      />

      {/* Modal card */}
      <div
        className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-[#C5366A]/10 animate-pop-in"
        style={{ animation: 'hackPopIn 350ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Close X */}
        <button
          type="button"
          onClick={() => dismiss('close_x')}
          aria-label="סגור"
          className="absolute top-3 left-3 w-9 h-9 rounded-full bg-[#FAFAFA] hover:bg-[#F0F0F0] text-[#6B6B6B] flex items-center justify-center transition-colors z-10"
          data-testid="hackathon-popup-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 sm:px-8 pt-7 pb-6 sm:pt-9 sm:pb-8">
          {/* Top tag */}
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#C5366A]/25 bg-[#C5366A]/[0.07] text-[#C5366A] text-[11px] sm:text-xs font-bold tracking-wide">
              <span aria-hidden>📅</span>
              30.6.26 | חיפה
            </span>
          </div>

          {/* H1 / H2 (28-32px desktop, ~22-24px mobile) */}
          <h2
            id="hack-popup-title"
            className="text-[22px] sm:text-[28px] md:text-[32px] font-black leading-[1.15] text-center text-[#2D2D2D] mb-3"
          >
            מנהל קמפיינים? תפסיק לבנות נדל&quot;ן ללקוחות שלך.
          </h2>

          {/* Subhead (20-22px desktop, ~17px mobile) in brand color */}
          <p className="text-[17px] sm:text-[20px] md:text-[22px] font-bold leading-snug text-center text-[#C5366A] mb-5">
            הצטרף להאקתון נכס דיגיטלי: &quot;מפיפיסיסט לבעל הון&quot;
          </p>

          {/* Body */}
          <p className="text-[15px] sm:text-[16px] md:text-[18px] text-[#4A4A4A] leading-relaxed text-center mb-5">
            4 שעות של וורקשופ פרקטי במתחם VIP בחיפה.
            אתה נכנס עם לפטופ, ויוצא עם מכונת אפיליאייט חיה באוויר שמכניסים אליה שקל – ומוציאים 3.
          </p>

          {/* Checkmarks */}
          <ul className="space-y-2.5 mb-7">
            {[
              'נטוורקינג של קמפיינרים',
              'ארכיטקטורת הנכס – מאפס לעמלה הראשונה',
              <>חינם לחברי מועדון <strong>&quot;מרד הפיפיסיסטים&quot;</strong></>,
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[14px] sm:text-[16px] text-[#2D2D2D]">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#C5366A]/12 border border-[#C5366A]/30 flex items-center justify-center mt-0.5">
                  <Check className="w-3 h-3 text-[#C5366A]" strokeWidth={3} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <a
            href={ctaHref}
            className="group inline-flex w-full items-center justify-center gap-2 bg-gradient-to-l from-[#C5366A] to-[#e84393] hover:from-[#b32d5d] hover:to-[#d63281] text-white py-4 sm:py-5 px-5 rounded-2xl font-black text-[17px] sm:text-[20px] shadow-[0_12px_30px_-8px_rgba(197,54,106,0.45)] hover:shadow-[0_16px_40px_-8px_rgba(197,54,106,0.6)] hover:-translate-y-0.5 active:translate-y-0 transition-all text-center"
            onClick={handleCtaClick}
            data-testid="hackathon-popup-cta"
          >
            <span>אני בפנים! שריינו לי מקום בהאקתון</span>
            <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
          </a>

          {/* Opt-out */}
          <button
            type="button"
            onClick={() => dismiss('opt_out_link')}
            className="block w-full mt-4 text-center text-[13px] sm:text-[14px] text-[#9CA3AF] hover:text-[#6B6B6B] underline underline-offset-2 transition-colors"
            data-testid="hackathon-popup-dismiss"
          >
            לא תודה, אני מעדיף להמשיך להתווכח עם לקוחות על הריטיינר שלי.
          </button>
        </div>
      </div>

      {/* Animations (scoped inline so no global CSS needed) */}
      <style>{`
        @keyframes hackPopIn {
          from { opacity: 0; transform: scale(0.92) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes hackFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: hackFadeIn 220ms ease-out; }
      `}</style>
    </div>
  );
};

export default HackathonPopup;
